import { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { User, Workspace, WorkspaceSettings } from '../types';
import {
  registerUser,
  loginUser,
  loginWithGoogle,
  logoutUser,
  subscribeToAuthChanges,
  RegisterData,
  CreateWorkspaceData,
  getUserData,
} from '../services/auth';
import { createWorkspace, updateWorkspace } from '../services/firestore';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: User | null;
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  switchWorkspace: (workspaceId: string) => void;
  createWorkspace: (data: CreateWorkspaceData) => Promise<void>;
  updateWorkspaceSettings: (settings: Partial<WorkspaceSettings>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; workspaces: Workspace[] } }
  | { type: 'LOGOUT' }
  | { type: 'SWITCH_WORKSPACE'; payload: Workspace }
  | { type: 'ADD_WORKSPACE'; payload: Workspace }
  | { type: 'UPDATE_WORKSPACE_SETTINGS'; payload: Partial<WorkspaceSettings> };

interface AuthState {
  user: User | null;
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  isAuthenticated: boolean;
  isLoading: boolean;
}

const initialState: AuthState = {
  user: null,
  currentWorkspace: null,
  workspaces: [],
  isAuthenticated: false,
  isLoading: true,
};

function ensureWorkspaceSettings(workspace: Workspace): Workspace {
  // Do not fill in any defaults, just ensure settings/branding exist as objects
  return {
    ...workspace,
    settings: {
      ...(workspace.settings || {}),
      branding: {
        ...((workspace.settings && workspace.settings.branding) || {})
      }
    }
  };
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'LOGIN_SUCCESS':
      const workspacesWithSettings = action.payload.workspaces.map(ensureWorkspaceSettings);
      return {
        ...state,
        user: action.payload.user,
        workspaces: workspacesWithSettings,
        currentWorkspace: workspacesWithSettings[0] || null,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    case 'SWITCH_WORKSPACE':
      return {
        ...state,
        currentWorkspace: ensureWorkspaceSettings(action.payload),
      };
    case 'ADD_WORKSPACE':
      return {
        ...state,
        workspaces: [...state.workspaces, ensureWorkspaceSettings(action.payload)],
        currentWorkspace: ensureWorkspaceSettings(action.payload),
      };
    case 'UPDATE_WORKSPACE_SETTINGS':
      if (!state.currentWorkspace) return state;
      
      const updatedWorkspace = ensureWorkspaceSettings({
        ...state.currentWorkspace,
        settings: {
          ...state.currentWorkspace.settings,
          ...action.payload,
          branding: {
            ...state.currentWorkspace.settings?.branding,
            ...action.payload.branding
          }
        },
        updatedAt: new Date().toISOString()
      });

      return {
        ...state,
        currentWorkspace: updatedWorkspace,
        workspaces: state.workspaces.map(w => 
          w.id === state.currentWorkspace?.id ? updatedWorkspace : w
        )
      };
    default:
      return state;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  // Load saved user data from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const savedWorkspaces = localStorage.getItem('workspaces');
    
    if (savedUser && savedWorkspaces) {
      const workspaces = JSON.parse(savedWorkspaces);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: JSON.parse(savedUser),
          workspaces: workspaces.map(ensureWorkspaceSettings)
        }
      });
    } else {
      // If no saved data, mark as not loading
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const saveWorkspacesAndBusinessSettings = (workspaces: Workspace[]) => {
    localStorage.setItem('workspaces', JSON.stringify(workspaces));
    // Save business settings for each workspace under a separate key
    const businessSettings = workspaces.map(ws => ({
      id: ws.id,
      branding: ws.settings?.branding || {},
      settings: ws.settings || {}
    }));
    localStorage.setItem('businessSettings', JSON.stringify(businessSettings));
  };

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          // Get user data from Firestore without trying to log in again
          const { user, workspaces } = await getUserData(firebaseUser.uid);
          // Update localStorage with latest data
          localStorage.setItem('user', JSON.stringify(user));
          saveWorkspacesAndBusinessSettings(workspaces);
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user, workspaces },
          });

          // If no workspaces exist, redirect to workspace setup
          if (workspaces.length === 0) {
            navigate('/setup');
          } else {
            // Only redirect to /app if we're on an auth page
            const isAuthPage = ['/signin', '/signup', '/forgot-password'].includes(window.location.pathname);
            if (isAuthPage) {
              navigate('/app');
            }
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        // User is signed out
        localStorage.removeItem('user');
        localStorage.removeItem('workspaces');
        dispatch({ type: 'LOGOUT' });
        
        // Only redirect to signin if we're trying to access a protected route
        const isProtectedRoute = window.location.pathname.startsWith('/app');
        if (isProtectedRoute) {
          navigate('/signin');
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { user, workspaces } = await loginUser(email, password);
      
      localStorage.setItem('user', JSON.stringify(user));
      saveWorkspacesAndBusinessSettings(workspaces);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, workspaces },
      });

      // Navigate based on workspace existence
      if (workspaces.length === 0) {
        navigate('/setup');
      } else {
        navigate('/app');
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const loginWithGoogleHandler = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const { user, workspaces } = await loginWithGoogle();
      // Save user data to localStorage
      localStorage.setItem('user', JSON.stringify(user));
      saveWorkspacesAndBusinessSettings(workspaces);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, workspaces },
      });
    } catch (error: unknown) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const { user, workspaces } = await registerUser(data);
      // Save user data to localStorage
      localStorage.setItem('user', JSON.stringify(user));
      saveWorkspacesAndBusinessSettings(workspaces);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, workspaces },
      });
    } catch (error: unknown) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutUser();
      // Clear localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('workspaces');
      // Clear all state
      dispatch({ type: 'LOGOUT' });
      // Navigate to home
      navigate('/');
    } catch (error: unknown) {
      console.error('Logout error:', error);
    }
  };

  const switchWorkspace = (workspaceId: string) => {
    const workspace = state.workspaces.find(w => w.id === workspaceId);
    if (workspace) {
      dispatch({ type: 'SWITCH_WORKSPACE', payload: workspace });
    }
  };

  const createWorkspaceHandler = async (data: CreateWorkspaceData) => {
    if (!state.user) return;

    try {
      // Create workspace with blank settings/branding, but all required fields present
      const workspaceData = {
        ...data,
        plan: 'free' as const,
        ownerId: state.user.id,
        members: [],
        settings: {
          currency: '',
          taxRate: 0,
          invoicePrefix: '',
          paymentMethods: [],
          branding: {
            primaryColor: '',
            companyName: '',
            address: '',
            email: '',
            phone: '',
            mobile: '',
            website: '',
            taxId: '',
            licenseNumber: '',
            city: '',
            postalCode: '',
            logo: ''
          }
        },
        updatedAt: new Date().toISOString()
      };

      const newWorkspace = await createWorkspace(workspaceData);
      dispatch({ type: 'ADD_WORKSPACE', payload: newWorkspace });
    } catch (error) {
      console.error('Error creating workspace:', error);
      throw error;
    }
  };

  const updateWorkspaceSettings = async (settings: Partial<WorkspaceSettings>) => {
    if (!state.currentWorkspace) return;

    try {
      const updatedSettings = {
        ...state.currentWorkspace.settings,
        ...settings,
        branding: {
          ...state.currentWorkspace.settings.branding,
          ...settings.branding
        }
      };

      await updateWorkspace(state.currentWorkspace.id, {
        settings: updatedSettings,
        updatedAt: new Date().toISOString()
      });

      dispatch({ type: 'UPDATE_WORKSPACE_SETTINGS', payload: settings });

      // Update localStorage immediately
      const updatedWorkspaces = state.workspaces.map(w =>
        w.id === state.currentWorkspace?.id
          ? { ...w, settings: updatedSettings }
          : w
      );
      saveWorkspacesAndBusinessSettings(updatedWorkspaces);
    } catch (error) {
      console.error('Error updating workspace settings:', error);
      throw error;
    }
  };

  const value = {
    ...state,
    login,
    loginWithGoogle: loginWithGoogleHandler,
    register,
    logout,
    switchWorkspace,
    createWorkspace: createWorkspaceHandler,
    updateWorkspaceSettings,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}